import { useState, useEffect, useMemo } from "react";
import { t } from "i18next";
import { useWorkorderParts, ExtendedWoPart, PartEditData } from "../../../../../hooks/parts/useWorkorderParts";
import { useWorkorderContext } from "../../../../../context/WorkorderContext";
import { useOdooConfig } from "../../../../../hooks/useOdooConfig";

import PartTableRow from "../../../../../components/workorders/parts/PartTableRow";
import DeleteConfirmDialog from "../../../../../components/workorders/parts/DeleteConfirmDialog";
import EmptyPartsState from "../../../../../components/workorders/parts/EmptyPartsState";
import PartTableHeader from "../../../../../components/workorders/parts/PartTableHeader";
import { toast } from "react-hot-toast";

/**
 * Affiche la liste des pièces
 * @param workorderId - ID du workorder
 * @param machineId - ID de la machine (vide si les pièces sont liées au workorder ou à un hose)
 * @param hoseId - ID du hose (vide si les pièces ne sont pas liées à un hose)
 * @param targetType - Type de cible (workorder, machine ou hose)
 */
export default function PartsSection({
  workorderId,
  machineId = "",
  hoseId,
  targetType = "workorder",
  canEdit = true
}: {
  workorderId: string;
  machineId?: string;
  hoseId?: string | undefined;
  targetType?: "workorder" | "machine" | "hose";
  canEdit?: boolean;
}) {
  // Utiliser le contexte du workorder
  const {
    workorder,
    inlineAdd,
    setInlineAdd,
    currentEditContext,
    editingPartId,
    setEditingPartId
  } = useWorkorderContext();

  // Identifiant unique pour cette instance basé sur son contexte (Pour permettre de gérer dans d'autre composant)
  const instanceId = useMemo(() => {
    if (targetType === "hose" && hoseId) {
      return `hose-${hoseId}`;
    } else if (targetType === "machine" && machineId) {
      return `machine-${machineId}`;
    } else {
      return `workorder-${workorderId}`;
    }
  }, [targetType, hoseId, machineId, workorderId]);

  // État local pour gérer l'ajout inline spécifique à cette instance
  const [localInlineAdd, setLocalInlineAdd] = useState(false);

  // État local pour les parts
  const [localParts, setLocalParts] = useState<ExtendedWoPart[]>([]);
  const [sortField, setSortField] = useState<string>("label");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // États pour la confirmation de suppression
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [partToDeleteId, setPartToDeleteId] = useState<string | null>(null);

  // État pour stocker temporairement les modifications avant sauvegarde
  const [editedPartData, setEditedPartData] = useState<PartEditData>({
    searchQuery: "",
    part: null,
    quantity: 1,
    rate: 0,
    price: 0,
    desk: false
  });

  // Récupérer les fonctions du hook
  const {
    removePart,
    saveEditedPart,
    saveDraftPart
  } = useWorkorderParts({
    workorderId,
    targetType,
    machineId,
    hoseId,
  });

  const { computeSellingPrice } = useOdooConfig();

  // Récupérer les parts du contexte
  const contextParts = useMemo(() => {
    if (targetType === "hose" && hoseId) {
      // Si on a un machineId, chercher d'abord dans les hoses de cette machine
      if (machineId) {
        const machine = workorder?.machines?.find(m => m.id === machineId);
        if (machine) {
          const hoseInMachine = machine.hoses?.find(h => h.id === hoseId);
          if (hoseInMachine && hoseInMachine.parts) {
            return hoseInMachine.parts;
          }
        }
      }

      // Si on n'a pas trouvé dans la machine ou pas de machineId,
      // chercher dans les hoses du workorder
      const hoseInWorkorder = workorder?.hoses?.find(h => h.id === hoseId);
      if (hoseInWorkorder && hoseInWorkorder.parts) {
        return hoseInWorkorder.parts;
      }

      // Si on n'a toujours pas trouvé, chercher dans toutes les machines
      for (const machine of workorder?.machines || []) {
        const hoseInMachine = machine.hoses?.find(h => h.id === hoseId);
        if (hoseInMachine && hoseInMachine.parts) {
          return hoseInMachine.parts;
        }
      }
    } else if (targetType === "machine" && machineId) {
      const machine = workorder?.machines?.find((m: any) => m.id === machineId);
      // Filtrer pour n'avoir que les parts directement liées à la machine (pas aux hoses)
      return machine?.parts?.filter((p: any) => !p.hose) || [];
    } else {
      // Parts du workorder
      return workorder?.parts || [];
    }
    return [];
  }, [workorder, targetType, machineId, hoseId]);

  // Fonction pour changer le tri
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Si on clique sur le même champ, inverser la direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Sinon, trier par le nouveau champ en ordre ascendant
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Écouter les changements sur inlineAdd du contexte global
  useEffect(() => {
    // Action concerne l'instance
    if (inlineAdd && currentEditContext === instanceId) {
      setLocalInlineAdd(true);
    } else if (!inlineAdd) {
      setLocalInlineAdd(false);
    }
  }, [inlineAdd, currentEditContext, instanceId]);

  // useEffect Ajouter ou supprimer des parts en mode brouillon
  useEffect(() => {
    if (localInlineAdd) {
      // Initialiser le brouillon avec un objet part vide mais valide
      setLocalParts(prev => [{
        id: "",
        part: null,
        quantity: 1,
        price: 0,
        desk: false,
        purchasePrice: 0,
        rate: 0,
        isDraft: true
      } as ExtendedWoPart, ...prev]);

      setEditedPartData({
        searchQuery: "",
        part: null,
        quantity: 1,
        rate: 0,
        price: 0,
        desk: false,
        purchasePrice: 0,
        documentReference: undefined,
        createdReference: undefined
      });
    } else {
      setLocalParts(prev => prev.filter(p => !p.isDraft));
    }
  }, [localInlineAdd]);

  // Trier les parts
  const filteredAndSortedParts = useMemo(() => {
    const result = [...localParts];

    return result.sort((a, b) => {
      // Brouillons en haut (Comme dans la maquette)
      if (a.isDraft && !b.isDraft) return -1;
      if (!a.isDraft && b.isDraft) return 1;

      // Tri
      if (!a.id && !b.id) return 0;
      if (!a.id) return 1;
      if (!b.id) return -1;

      let valueA, valueB;
      const priceA = a.desk ? (a.price || 0) : (a.part?.price || 0);
      const priceB = b.desk ? (b.price || 0) : (b.part?.price || 0);
      // Déterminer les valeurs à comparer selon le champ de tri
      switch (sortField) {
        case "label":
          valueA = a.part?.label || "";
          valueB = b.part?.label || "";
          break;
        case "reference":
          valueA = a.part?.ref || "";
          valueB = b.part?.ref || "";
          break;
        case "quantity":
          valueA = a.quantity || 0;
          valueB = b.quantity || 0;
          break;
        case "purchasePrice":
          valueA = a.desk ? (a.purchasePrice || 0) : (a.part?.purchasePrice || 0);
          valueB = b.desk ? (b.purchasePrice || 0) : (b.part?.purchasePrice || 0);
          break;
        case "price":
          valueA = priceA;
          valueB = priceB;
          break;
        case "total":
          valueA = (a.quantity || 0) * (priceA || 0);
          valueB = (b.quantity || 0) * (priceB || 0);
          break;
        default:
          valueA = a.id;
          valueB = b.id;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      } else {
        const strA = String(valueA);
        const strB = String(valueB);
        return sortDirection === "asc"
          ? strA.localeCompare(strB)
          : strB.localeCompare(strA);
      }
    });
  }, [localParts, sortField, sortDirection]);

  // Mettre à jour les parts locales lorsque les parts du contexte changent
  useEffect(() => {
    setLocalParts(prev => {
      const drafts = prev.filter(p => p.isDraft);
      return [...drafts, ...(contextParts || [])];
    });
  }, [contextParts]);

  /**
   * Ouvre le dialogue de confirmation de suppression
   */
  const handleDeleteClick = (partIdOnWo: string): void => {
    setPartToDeleteId(partIdOnWo);
    setIsConfirmDialogOpen(true);
  };

  /**
   * Ferme le dialogue de confirmation de suppression
   */
  const handleCancelDelete = (): void => {
    setPartToDeleteId(null);
    setIsConfirmDialogOpen(false);
  };

  /**
   * Confirme la suppression d'une pièce
   */
  const handleConfirmDelete = async (): Promise<void> => {
    if (partToDeleteId) {
      // Assurer que tous les paramètres sont du bon type
      const currentTargetType = targetType || "workorder";
      const currentMachineId = machineId || "";

      await removePart(
        partToDeleteId,
        currentTargetType,
        currentMachineId,
        hoseId
      );

      setIsConfirmDialogOpen(false);
      setPartToDeleteId(null);
    }
  };

  // Fonction pour passer un part en mode édition
  const handleEditPart = (part: ExtendedWoPart) => {
    // Si on est déjà en train d'ajouter une pièce, on ne peut pas éditer
    if (inlineAdd) return;

    // Mettre à jour l'ID du part en édition
    setEditingPartId(part.id);

    // Initialiser les données d'édition avec les valeurs actuelles du part
    setEditedPartData({
      searchQuery: part.part?.label || "",
      part: part.part,
      quantity: part.quantity,
      rate: part.rate || 0,
      price: part.price,
      desk: part.desk,
      purchasePrice: part.purchasePrice || (part.part?.purchasePrice || 0),
      documentReference: part.documentReference,
      // Comment la référence a été obtenue est une propriété de la ligne : la modifier ici ne
      // doit pas la réécrire, sans quoi éditer une ligne saisie sur mobile la ferait basculer.
      createdReference: part.createdReference
    });
  };

  // Fonction pour annuler l'édition
  const handleCancelEdit = () => {
    setEditingPartId(null);
    setEditedPartData({
      searchQuery: "",
      part: null,
      quantity: 1,
      rate: 0,
      price: 0,
      desk: false,
      purchasePrice: 0
    });
  };

  // Fonction pour sauvegarder les modifications d'un part
  const handleSaveEdit = async () => {
    if (!editingPartId) return;

    const success = await saveEditedPart(editingPartId, editedPartData);
    if (success) {
      setEditingPartId(null);
    }
  };

  // Fonction pour sauvegarder un part draft
  const handleSaveDraft = async (draftPart: ExtendedWoPart) => {

    // Étalé plutôt qu'énuméré champ par champ : la liste explicite qu'il y avait ici laissait
    // tomber en silence tout champ ajouté ensuite à PartEditData — c'est exactement ce qui est
    // arrivé à la référence du bon de commande.
    const partToSave = {
      ...draftPart,
      ...editedPartData,
    };

    // Vérifions si la part est correctement définie
    // Pour les pièces desk, l'ID n'est pas requis à ce stade (sera créé par le hook)
    if (!partToSave.part || (!partToSave.desk && !partToSave.part.id)) {
      toast.error(t("workorder:parts:error:partInvalid"));
      return false;
    }

    const success = await saveDraftPart(partToSave);
    if (success) {
      setInlineAdd(false);
    }

    return success;
  };

  // Gestionnaire pour la sélection d'une part dans la recherche
  const handleSelectPart = (part: EasyTech.Part) => {

    // Champs nécessaires présents
    if (!part || !part.id) {
      console.error("Selected part is invalid:", part);
      return;
    }

    const rate = part.price || 0;
    const price = rate;

    setEditedPartData(prev => ({
      ...prev,
      part: part,
      searchQuery: part.label || "",
      rate: rate,
      price: price,
      purchasePrice: part.purchasePrice || 0
    }));
  };

  // Gestionnaire pour le changement de la requête de recherche
  const handleSearchQueryChange = (query: string) => {
    setEditedPartData(prev => ({
      ...prev,
      searchQuery: query
    }));
  };

  return (
    <div className="parts-section">
      <div className="flex flex-col gap-4">
        {/* Tableau des pièces */}
        {filteredAndSortedParts.length > 0 ? (

          <div className="w-full overflow-hidden border border-gray-200 rounded-lg shadow-table mb-4">
            <div className="w-full overflow-x-auto">
              <table className="w-full table-fixed text-left">
              <PartTableHeader
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <tbody>
                {filteredAndSortedParts.map((partItem, index) => {
                  const isDraft = !partItem.id && partItem.isDraft;
                  const isEditing = partItem.id === editingPartId;

                  const allPartIds = filteredAndSortedParts
                    .map(p => (p.id === editingPartId || (p.isDraft && isDraft)) ? editedPartData.part?.id : p.part?.id)
                    .filter(Boolean) as string[];
                  const existingPartIds = allPartIds.filter(id => id !== (isDraft ? editedPartData.part?.id : partItem.part?.id));

                  // Pour le mode brouillon, utilisons editedPartData
                  const partData = isDraft ? {
                    ...partItem,
                    part: editedPartData.part
                  } : partItem;

                  return (
                    <PartTableRow
                      key={partItem.id || index}
                      partItem={partData}
                      index={index}
                      workorderId={workorderId}
                      machineId={machineId}
                      hoseId={hoseId}
                      targetType={targetType}
                      isEditing={isEditing}
                      isDraft={isDraft || false}
                      existingPartIds={existingPartIds}
                      editedPartData={isEditing || isDraft ? editedPartData : undefined}
                      setEditedPartData={isEditing || isDraft ? setEditedPartData : undefined}
                      onEditClick={handleEditPart}
                      onDeleteClick={handleDeleteClick}
                      onSaveClick={isDraft ? () => handleSaveDraft(partData) : handleSaveEdit}
                      onCancelClick={isDraft ? () => setInlineAdd(false) : handleCancelEdit}
                      onSearchQueryChange={handleSearchQueryChange}
                      onSelectPart={handleSelectPart}
                      canEdit={canEdit && !inlineAdd}
                      computeSellingPrice={computeSellingPrice}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        ) : (
          <EmptyPartsState />
        )}
      </div>

      {/* Dialogue de confirmation de suppression */}
      <DeleteConfirmDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
